import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api, API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Heart, MessageCircle, Send, Flame as FlameIcon, Drum, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import AudioRecorder from '@/components/AudioRecorder';
import VideoRecorder from '@/components/VideoRecorder';
import { useT } from '@/context/LocaleContext';

const REACTIONS = [
  { key: 'heart', Icon: Heart, label: '❤️' },
  { key: 'fire', Icon: FlameIcon, label: '🔥' },
  { key: 'drum', Icon: Drum, label: '🥁' },
];

export default function Community() {
  const { user } = useAuth();
  const { t } = useT();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState('');
  const [audio, setAudio] = useState(null);
  const [video, setVideo] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [tagChallenge, setTagChallenge] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});

  const load = async () => {
    const [pRes, cRes] = await Promise.all([
      api.get('/posts'),
      api.get('/challenges/current'),
    ]);
    setPosts(pRes.data);
    setChallenge(cRes.data?.challenge || null);
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!content.trim() && !audio && !video) return;
    try {
      await api.post('/posts', {
        content,
        audio_data: audio?.base64,
        audio_mime: audio?.mime,
        video_data: video?.base64,
        video_mime: video?.mime,
        challenge_id: tagChallenge && challenge ? challenge.id : null,
      });
      setContent('');
      setAudio(null);
      setVideo(null);
      setTagChallenge(false);
      load();
      toast.success('Publié !');
    } catch { toast.error('Erreur lors de la publication'); }
  };

  const like = async (id) => {
    await api.post(`/posts/${id}/like`);
    load();
  };

  const react = async (id, reaction) => {
    await api.post(`/posts/${id}/reaction`, { reaction });
    load();
  };

  const comment = async (id) => {
    const c = commentDrafts[id];
    if (!c?.trim()) return;
    await api.post(`/posts/${id}/comment`, { content: c });
    setCommentDrafts(d => ({ ...d, [id]: '' }));
    load();
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl">
        <div className="mb-8 rise">
          <div className="overline text-orange-400 mb-2">{t('nav.community')}</div>
          <h1 className="font-display text-5xl tracking-tight">{t('community.share_practice')}</h1>
        </div>

        <div className="card-stone p-5 mb-8">
          <div className="flex items-start gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-stone-700" />
            )}
            <div className="flex-1">
              <textarea
                data-testid="new-post-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Raconte ta séance, pose une question, partage une reprise..."
                rows={3}
                className="w-full resize-none px-3 py-2 rounded-xl bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <AudioRecorder onRecorded={setAudio} />
                <VideoRecorder onRecorded={setVideo} />
              </div>
              {challenge && (
                <label className="mt-3 flex items-center gap-2 text-sm text-stone-300 cursor-pointer" data-testid="tag-challenge-wrap">
                  <input
                    type="checkbox"
                    data-testid="tag-challenge-check"
                    checked={tagChallenge}
                    onChange={(e) => setTagChallenge(e.target.checked)}
                    className="accent-orange-600 w-4 h-4"
                  />
                  <Trophy size={14} className="text-orange-400" />
                  Inscrire au défi de la semaine ({challenge.song_title})
                </label>
              )}
              <div className="flex justify-end mt-3">
                <button data-testid="publish-post-btn" onClick={post} className="btn-primary text-sm">{t('community.publish')}</button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {posts.map((p, idx) => (
            <div key={p.id} data-testid={`post-${idx}`} className="card-stone p-5 rise">
              <div className="flex items-center gap-3">
                {p.user_picture ? (
                  <img src={p.user_picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-stone-700 flex items-center justify-center text-sm">{p.user_name?.[0] || '?'}</div>
                )}
                <div>
                  <div className="text-sm font-medium">{p.user_name}</div>
                  <div className="text-xs text-stone-500">{new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                {p.instrument && (
                  <span className="ml-auto text-xs px-2 py-1 rounded-full bg-orange-700/20 border border-orange-800/40 text-orange-300">{p.instrument}</span>
                )}
              </div>
              <p className="mt-3 text-stone-200 whitespace-pre-wrap">{p.content}</p>

              {p.audio_url && (
                <div className="mt-3">
                  <audio data-testid={`post-audio-${idx}`} controls src={`${API.replace(/\/api$/, '')}${p.audio_url}`} className="w-full" />
                </div>
              )}
              {p.video_url && (
                <div className="mt-3 rounded-xl overflow-hidden border border-stone-800 max-w-md">
                  <video data-testid={`post-video-${idx}`} controls src={`${API.replace(/\/api$/, '')}${p.video_url}`} className="w-full" />
                </div>
              )}
              {p.challenge_id && (
                <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-orange-700/20 border border-orange-800/40 text-orange-300">
                  <Trophy size={10} /> Défi hebdo
                </div>
              )}

              <div className="mt-4 flex items-center gap-4 text-sm text-stone-400 flex-wrap">
                <button data-testid={`like-btn-${idx}`} onClick={() => like(p.id)} className={`inline-flex items-center gap-1 hover:text-orange-400 ${p.liked_by?.includes(user?.user_id) ? 'text-orange-400' : ''}`}>
                  <Heart size={14} fill={p.liked_by?.includes(user?.user_id) ? 'currentColor' : 'none'} /> {p.likes || 0}
                </button>
                <div className="inline-flex items-center gap-1"><MessageCircle size={14} /> {p.comments?.length || 0}</div>
                <div className="flex items-center gap-2 ml-auto">
                  {REACTIONS.map(({ key, Icon, label }) => {
                    const users = p.reactions?.[key] || [];
                    const active = users.includes(user?.user_id);
                    return (
                      <button
                        key={key}
                        data-testid={`reaction-${key}-${idx}`}
                        onClick={() => react(p.id, key)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs transition-colors ${
                          active ? 'border-orange-600 bg-orange-950/40 text-orange-300' : 'border-stone-700 hover:border-stone-500'
                        }`}
                        title={label}
                      >
                        <Icon size={12} fill={active ? 'currentColor' : 'none'} />
                        <span>{users.length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {p.comments?.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-stone-800 pt-3">
                  {p.comments.map((c, ci) => (
                    <div key={c.id || ci} className="text-sm">
                      <span className="text-stone-300 font-medium">{c.user_name}</span>
                      <span className="text-stone-400 ml-2">{c.content}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid={`comment-input-${idx}`}
                  value={commentDrafts[p.id] || ''}
                  onChange={(e) => setCommentDrafts(d => ({ ...d, [p.id]: e.target.value }))}
                  placeholder="Ajouter un commentaire..."
                  className="flex-1 px-3 py-2 rounded-full bg-stone-900 border border-stone-800 text-sm text-stone-100 focus:outline-none focus:border-orange-800"
                />
                <button data-testid={`comment-send-${idx}`} onClick={() => comment(p.id)} className="p-2 rounded-full bg-stone-800 hover:bg-stone-700"><Send size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
